/**
 * AppDownloadButtons — both store buttons, ordered by the detected platform.
 *
 * Composes the pure link builders (`@butter/library/shared`) with device
 * detection and the App Store / Google Play buttons. When a platform has no
 * dedicated tenant app it falls back to the generic Butter app and surfaces the
 * tenant `handle` registration tips.
 *
 * This is the default download UI (show both, detected platform first). For a
 * single call-to-action use `SmartStoreButton`.
 *
 * @module butterUI/domains/app/AppDownloadButtons
 */

import { generateAppStoreLinks } from '@butter/library/shared';
import type { TenantAppConfig } from '@butter/library/shared';
import { cn } from '#utils';
import { getDeviceType, type DeviceType } from '../utils';
import { AppStoreButton, PlayStoreButton } from '../StoreButtons';

export interface AppDownloadButtonsProps {
  /** Tenant store ids; omit for the generic Butter app on both platforms. */
  tenant?: TenantAppConfig;
  /** Force detected-platform ordering instead of sniffing (testing / stories). */
  device?: DeviceType;
  /**
   * Show the small "iOS/Android device detected" note under the buttons. This is
   * an internal debug affordance — consumers should gate it to non-production
   * (dev/staging).
   * @default false
   */
  showDetectedNote?: boolean;
  /**
   * Play install referrer for the generic app (`clubInstallReferrer`): carries the
   * club through a fresh install so the app opens with it preselected.
   */
  referrer?: string;
  /**
   * A deep link to this page in the app (`buildAppLink`). Shown on iOS only, as
   * "Already have the app? Open it" — a tap to a different host, so an installed app
   * opens; iOS has no install referrer, so this is how a fresh install gets the club.
   * Android carries the club through `referrer` instead, and its app links may not be
   * verified yet, where the tap would only bounce back to this page.
   */
  openAppUrl?: string;
  className?: string;
}

/** Tenant handle shown in a mono highlight. */
function HandleMono({ handle }: { handle: string }) {
  return <span className="font-mono font-semibold text-theme-600">{handle}</span>;
}

/** "Use handle X to register in the app" tip under a generic-fallback button. */
function HandleTip({ handle }: { handle: string }) {
  return (
    <p className="mt-2 text-center text-xs text-gray-600">
      Use handle <HandleMono handle={handle} /> to register in the app
    </p>
  );
}

export function AppDownloadButtons({
  tenant,
  device,
  showDetectedNote = false,
  referrer,
  openAppUrl,
  className,
}: AppDownloadButtonsProps) {
  const links = generateAppStoreLinks(tenant, { referrer });
  const resolved = device ?? getDeviceType();
  const handle = tenant?.handle;

  // Per-platform handle tip only when THAT platform is generic but the other is
  // not (a fully-generic app uses the footer note instead).
  const showIOSHandleTip = links.isIOSGeneric && !links.isGenericApp && !!handle;
  const showAndroidHandleTip = links.isAndroidGeneric && !links.isGenericApp && !!handle;
  const showFooterHandleTip = links.isGenericApp && !!handle;

  const iosBlock = (
    <div key="ios">
      <AppStoreButton href={links.ios} className="w-full" />
      {showIOSHandleTip && handle && <HandleTip handle={handle} />}
    </div>
  );

  const androidBlock = (
    <div key="android">
      <PlayStoreButton href={links.android} className="w-full" />
      {showAndroidHandleTip && handle && <HandleTip handle={handle} />}
    </div>
  );

  // iOS first everywhere except Android devices.
  const ordered = resolved === 'android' ? [androidBlock, iosBlock] : [iosBlock, androidBlock];

  return (
    <div className={cn('space-y-4', className)}>
      <div className="space-y-3">{ordered}</div>

      {openAppUrl && resolved === 'ios' && (
        <p className="text-center text-xs text-gray-600">
          Already have the app?{' '}
          <a href={openAppUrl} className="font-semibold text-theme-600 underline" data-testid="open-app-link">
            Open it
          </a>
        </p>
      )}

      {showFooterHandleTip && handle ? (
        <div className="mt-4 space-y-2 border-t border-gray-200 pt-4">
          <p className="text-center text-sm font-medium text-gray-700">After downloading:</p>
          <p className="text-center text-sm text-gray-600">
            Use handle <HandleMono handle={handle} /> to get started
          </p>
        </div>
      ) : (
        showDetectedNote &&
        resolved !== 'desktop' && (
          <p className="pt-2 text-center text-xs text-gray-400">
            {resolved === 'ios' ? 'iOS device detected' : 'Android device detected'}
          </p>
        )
      )}
    </div>
  );
}
